import { useEffect } from "react";

const PrivacyPolicy = () => {
    // Set page title
  useEffect(() => {
    document.title = "Privacy Policy - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  return (
    <div className="legal-page" style={{ color: '#000000' }}>
      <div className="legal-container">
        <div className="legal-content" style={{ color: '#000000' }}>
          <h1 className="legal-title gold-text">Privacy Policy</h1>
          <p className="legal-updated">Last Updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              AI for Law Firms ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains 
              how we collect, use, disclose, and safeguard your information when you use our Platform.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Information You Provide</h3>
            <p>We collect information that you provide directly to us, including:</p>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, law firm name, jurisdiction, and country</li>
              <li><strong>Documents:</strong> Files and documents you upload to the Platform for analysis</li>
              <li><strong>Communications:</strong> Messages, feature requests, and support inquiries</li>
              <li><strong>Usage Data:</strong> Information about how you interact with Lawra and use our tools</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>When you use the Platform, we automatically collect:</p>
            <ul>
              <li>Device information (browser type, operating system)</li>
              <li>IP address and location data</li>
              <li>Usage patterns and preferences</li>
              <li>Session information and analytics data</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve our AI services</li>
              <li>Process and analyze documents you upload</li>
              <li>Personalize your experience with Lawra</li>
              <li>Communicate with you about updates, features, and support</li>
              <li>Monitor and analyze usage patterns to improve our services</li>
              <li>Protect against unauthorized access and abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. AI Processing and Third-Party Services</h2>
            <p>
              Our Platform uses artificial intelligence services (including OpenAI's GPT models) to process your documents 
              and provide analysis. When you use our AI features:
            </p>
            <ul>
              <li>Your documents and queries are sent to our AI service providers for processing</li>
              <li>These providers process data according to their own privacy policies</li>
              <li>We use commercially reasonable efforts to ensure data security</li>
              <li>AI providers do not use your data to train their models (as per enterprise agreements)</li>
            </ul>
            <p className="legal-important">
              <strong>Important:</strong> Do not upload documents containing highly sensitive or confidential information 
              unless you understand and accept the risks of AI processing.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share your information with:</p>
            <ul>
              <li><strong>Service Providers:</strong> AI providers, hosting services, and analytics platforms that help us operate</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your information, including:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>Secure data storage practices</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive 
              to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Data Retention</h2>
            <p>
              We retain your information for as long as necessary to provide our services and comply with legal obligations. 
              You may request deletion of your account and associated data at any time, subject to legal retention requirements.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing of your information</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw consent for data processing</li>
            </ul>
            <p>To exercise these rights, please contact us through the Platform.</p>
          </section>

          <section className="legal-section">
            <h2>9. Cookies and Tracking</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your experience, analyze usage, and remember 
              your preferences. You can control cookies through your browser settings.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Children's Privacy</h2>
            <p>
              Our Platform is not intended for individuals under 18 years of age. We do not knowingly collect personal 
              information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have different data protection laws. We ensure appropriate safeguards are in place for such transfers.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting 
              the new policy on the Platform and updating the "Last Updated" date.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us 
              through the Platform's support channels.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
